import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {lerpGreenToRedViaOrange, scaleNumber} from "../../helpers/scaling";
import {clearDiv} from "../../helpers/builders";
import {formatTimeRange, enableTooltips, disposeTooltips, unixToISO} from "../../helpers/tooltips";
import {buildHeatmapIssues} from "../../helpers/heatmap_explain";
import {debugLog} from '../../helpers/debug_log';

export class ShaperSiteRttHeatmap extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.explainIssues = null;
    }

    title() {
        return "RTT Heatmap";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Site Heatmap — RTT", issues: this.explainIssues };
    }

    channelRequests() {
        return [ "SitePage" ];
    }

    buildContainer() {
        let base = super.buildContainer();
        base.style.overflow = "auto";
        let content = document.createElement("div");
        content.style.width = "100%";
        content.id = "rttHeatmap_" + this.slot;
        content.classList.add("text-center", "text-muted", "small", "lqos-heatmap-fill");
        this.contentId = content.id;
        base.appendChild(content);
        return base;
    }

    setup() {
        super.setup();
        $("#" + this.contentId).html("<i class='fa fa-spinner fa-spin'></i> Loading...");
    }

    onMessage(msg) {
        if (msg.Data.target !== "site_bundle") return;

        // Display Render Status
        // TODO: Promote this to a base class
        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }

        // The data is found in msg.Data.blob.site_hash
        debugLog(msg);
        let sites = msg.Data.blob[0];
        let circuits = msg.Data.blob[1];
        let n_rows = 0;
        for (const [siteHash, value] of Object.entries(sites)) {
            n_rows = Math.max(n_rows, value.body.ticks.length);
        }
        for (const [_siteHash, value] of Object.entries(circuits)) {
            n_rows = Math.max(n_rows, value.body.ticks.length);
        }
        const DESIRED_COLUMNS = 10;
        let group_size = Math.max(1, Math.ceil(n_rows / DESIRED_COLUMNS));
        let result = [];
        for (const [siteHash, value] of Object.entries(sites)) {
            let site = {
                name: value.header.name,
                hash: siteHash,
                type: 'site',
                vals: [],
                ticks: value.body.ticks,
                ranges: [],
            };
            const rttSeries = value.body.median_rtt_median || [];
            for (let i=0; i<n_rows; i+=group_size) {
                let bucket = [];
                for (let j=0; j<group_size; j++) {
                    // Avoid out of bounds
                    if (i+j >= rttSeries.length) {
                        break;
                    }
                    const v = rttSeries[i+j];
                    if (Number.isFinite(v)) {
                        bucket.push(v / 10);
                    }
                }
                let med = medianFinite(bucket);
                site.vals.push(med);
                if (site.ticks && site.ticks.length > 0 && i < site.ticks.length) {
                    const startIdx = i;
                    const endIdx = Math.min(i + group_size - 1, site.ticks.length - 1);
                    site.ranges.push({ start: site.ticks[startIdx], end: site.ticks[endIdx] });
                } else {
                    site.ranges.push(null);
                }
            }
            if (site.vals.some((v) => v !== null)) {
                result.push(site);
            }
            debugLog(site);
        }

        for (const [siteHash, value] of Object.entries(circuits)) {
            let site = {
                name: value.header.name,
                hash: siteHash,
                type: 'circuit',
                vals: [],
                ticks: value.body.ticks,
                ranges: [],
            };
            const rttSeries = value.body.median_rtt_median || [];
            for (let i=0; i<n_rows; i+=group_size) {
                let bucket = [];
                for (let j=0; j<group_size; j++) {
                    // Avoid out of bounds
                    if (i+j >= rttSeries.length) {
                        break;
                    }
                    const v = rttSeries[i+j];
                    if (Number.isFinite(v)) {
                        bucket.push(v / 10);
                    }
                }
                let med = medianFinite(bucket);
                site.vals.push(med);
                if (site.ticks && site.ticks.length > 0 && i < site.ticks.length) {
                    const startIdx = i;
                    const endIdx = Math.min(i + group_size - 1, site.ticks.length - 1);
                    site.ranges.push({ start: site.ticks[startIdx], end: site.ticks[endIdx] });
                } else {
                    site.ranges.push(null);
                }
            }
            if (site.vals.some((v) => v !== null)) {
                result.push(site);
            }
            debugLog(site);
        }

        // Clear the div
        let target = document.getElementById(this.contentId);
        disposeTooltips(target);
        clearDiv(target);

        // Build the table
        let table = document.createElement("table");
        table.classList.add("small", "muted", "lqos-heatmap-table");

        result.forEach((row) => {
            let tr = document.createElement("tr");
            tr.addEventListener("mouseenter", () => tr.classList.add("lqos-heatmap-row-hover"));
            tr.addEventListener("mouseleave", () => tr.classList.remove("lqos-heatmap-row-hover"));
            let title = document.createElement("td");
            title.classList.add("lqos-heatmap-label");
            title.title = row.name;
            let name = row.name;
            if (name.length > 12) {
                name = name.substring(0, 12) + "...";
            }
            let link = document.createElement("a");
            if (row.type === 'circuit') {
                link.href = `shaper_circuit.html?circuit=${row.hash}&nodeId=${window.nodeId}`;
            } else {
                link.href = `shaper_site.html?site=${row.hash}&nodeId=${window.nodeId}`;
            }
            link.appendChild(document.createTextNode(name));
            link.classList.add("redactable");
            link.title = row.name;
            title.appendChild(link);
            title.style.textAlign = "right";
            tr.appendChild(title);

            for (let i=0; i<row.vals.length; i++) {
                let cell = row.vals[i];
                let cellElement = document.createElement("td");
                cellElement.classList.add("lqos-heatmap-cell");
                let rtt = cell;
                
                // Add Bootstrap tooltip with time range and value
                const range = Array.isArray(row.ranges) ? row.ranges[i] : null;
                let rangeStart = null;
                let rangeEnd = null;
                if (range && Number.isFinite(range.start) && Number.isFinite(range.end)) {
                    rangeStart = range.start;
                    rangeEnd = range.end;
                } else if (row.ticks && row.ticks.length > 0) {
                    const startIdx = Math.min(i * group_size, row.ticks.length - 1);
                    const endIdx = Math.min(startIdx + group_size - 1, row.ticks.length - 1);
                    rangeStart = row.ticks[startIdx];
                    rangeEnd = row.ticks[endIdx];
                }
                if (rangeStart !== null && rangeEnd !== null) {
                    const timeRange = formatTimeRange(rangeStart, rangeEnd);
                    const valueStr = rtt !== null ? `${rtt.toFixed(0)}ms` : 'No Data';
                    cellElement.setAttribute('data-bs-toggle', 'tooltip');
                    cellElement.setAttribute('data-bs-title', `${timeRange}\nMedian RTT: ${valueStr}\n\nClick to zoom to this time period`);
                    cellElement.style.cursor = "pointer";
                    
                    // Add click handler to set time period
                    cellElement.onclick = () => {
                        if (window.timePicker) {
                            const start = unixToISO(rangeStart);
                            const end = unixToISO(rangeEnd + 60); // +1 minute buffer
                            window.timePicker.absoluteTime(start, end);
                        }
                    };
                }
                
                if (rtt === null) {
                    cellElement.classList.add("lqos-heatmap-cell--empty");
                    cellElement.style.background = "#444";
                    cellElement.appendChild(document.createTextNode("-"));
                } else {
                    cellElement.appendChild(document.createTextNode(rtt.toFixed(0)));
                    cellElement.style.background = lerpGreenToRedViaOrange(200 - rtt, 200);
                    cellElement.style.color = "black";
                }
                tr.appendChild(cellElement);
            }
            table.appendChild(tr);
        });

        this.explainIssues = buildHeatmapIssues({
            rows: result,
            metricLabel: "RTT",
            maxValue: 200,
            higherIsWorse: true,
            topN: 3,
            formatValue: (v) => `${v.toFixed(0)} ms`,
            includeRowName: true,
        });

        // Add it
        target.appendChild(table);
        
        // Initialize Bootstrap tooltips
        enableTooltips(target);

        this.rendered = true;
    }
}

function medianFinite(values) {
    const data = values.filter((v) => Number.isFinite(v)).slice().sort((a, b) => a - b);
    if (data.length === 0) return null;
    const mid = Math.floor(data.length / 2);
    if (data.length % 2 === 1) {
        return data[mid];
    }
    return (data[mid - 1] + data[mid]) / 2;
}
